import { Card, CardContent, Typography, Box, useTheme, Tooltip } from '@mui/material';
import type { VendaVendedorItem } from '@azoupvendas/shared';

interface Props {
  dados: VendaVendedorItem[];
}

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0
  });
}

// AIDEV-NOTE: Lista ranqueada de todos os vendedores, sem limite, estilo idêntico ao TopClientesChart
export function VendasVendedorChart({ dados }: Props) {
  const theme = useTheme();

  const sorted = [...dados].sort((a, b) => b.total - a.total);
  const maxTotal = sorted[0]?.total ?? 1;

  if (sorted.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Ranking de vendedores
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nenhum pedido no período
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Ranking de vendedores
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {sorted.map((v, index) => {
            const pct = (v.total / maxTotal) * 100;
            const isTop3 = index < 3;
            return (
              <Box key={index}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.4 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 20,
                      fontWeight: 700,
                      color: isTop3 ? theme.palette.secondary.main : 'text.disabled'
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Tooltip title={v.nome} placement="top-start" enterDelay={400}>
                    <Typography
                      variant="body2"
                      fontWeight={isTop3 ? 600 : 400}
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {v.nome}
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {v.pedidos} ped.
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ whiteSpace: 'nowrap', color: isTop3 ? 'text.primary' : 'text.secondary' }}
                  >
                    {formatCurrency(v.total)}
                  </Typography>
                </Box>
                <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'grey.100', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${pct}%`,
                      borderRadius: 2,
                      bgcolor: isTop3
                        ? theme.palette.secondary.main
                        : theme.palette.secondary.light,
                      transition: 'width 0.4s ease'
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}
